import { ClientEvents } from 'common/events/ClientEvents';
import { adjustedStarsToRating } from 'common/RatingUtilities';
import React, { useState } from 'react';
import {
  DietaryDetails,
  DietaryDetailsForShefDocument,
  DietaryDetailsForShefQuery,
  OrderShefFragment,
} from 'src/gqlReactTypings.generated.d';
import { useGotoReviews } from 'src/pages/consumer/order/components/ReviewsModal/useGotoReviews';
import { DescriptionCard } from 'src/pages/consumer/order/components/ShefDescriptionCards/DescriptionCard';
import { DietaryDescriptionCard } from 'src/pages/consumer/order/components/ShefDescriptionCards/verified-dietary/DietaryDescriptionCard';
import { BowlWithWhisk, FoodSafetyCertified, StarV2 } from 'src/shared/design-system/Icon';
import { Tooltip } from 'src/shared/design-system/Tooltip';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useGqlClient } from 'src/shared/useGqlClient';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled from 'styled-components';
import { ScreenWidth, Spacing } from 'web-common/src/shared/styles';

export interface ShefDescriptionChipsProps {
  isTrialShef: boolean;
  mehkoLicenseNumber: string | null;
  numOrders: number;
  numRatings: number;
  isFHCApproved: boolean;
  averageRating: number;
}

const ShefDescriptionCardsContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  margin-top: -16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: ${Spacing.HALF};

  scroll-padding-left: 16px;

  & > * {
    scroll-snap-align: start;
  }

  // hide scroll bar
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }

  ${minScreen(ScreenWidth.SMALL)} {
    margin-left: -16px;
    // Note: If we add enough cards on large screens we should just wrap onto multiple rows
    // This doesn't look _great_ though, so we should design for a max of one row
    flex-wrap: wrap;
    overflow-x: unset;
  }
`;

const formatThousandsNumber = (num: number): string => {
  if (num >= 1000) {
    return `${Math.round(num / 100) / 10}k+`;
  }

  return num.toString();
};

export const ShefNumReviewsCard = ({
  averageRating,
  numRatings,
  onClick,
}: {
  averageRating: number;
  numRatings: number;
  onClick?: () => void;
}) => (
  <DescriptionCard
    headline={`${adjustedStarsToRating(averageRating)}`}
    icon={
      <div className='mt-px'>
        <StarV2 width={12} height={12} />
      </div>
    }
    description={`${formatThousandsNumber(numRatings)} ${numRatings === 1 ? 'review' : 'reviews'}`}
    onClick={onClick}
  />
);

const FoodSafetyCard = () => (
  <Tooltip
    content={
      <span>
        This shef has obtained a food safety certificate from an ANSI-accredited food safety training provider.
      </span>
    }>
    <DescriptionCard
      headline='Certified'
      icon={<FoodSafetyCertified height={16} width={16} />}
      description='Food safety'
    />
  </Tooltip>
);

interface IProps {
  className?: string;
  shef: OrderShefFragment;
}

export const DescriptionCardsRow: React.FC<IProps> = ({ className, shef }): JSX.Element => {
  const GqlClient = useGqlClient();
  const tracker = useTracker();
  const gotoReviews = useGotoReviews();

  const { shefProfile, totalOrders, rating } = shef;
  const { fhcApprovedAt, mehkoLicenseNumber } = shefProfile;
  const averageRating = rating?.score ?? 0;
  const numRatings = rating?.count ?? 0;
  const [dietaryDetails, setDietaryDetails] = useState<DietaryDetails[]>();

  const handleReviewsClick = () => {
    tracker.track(ClientEvents.CLICK_REVIEWS_BUTTON, { position: 'bio' });
    gotoReviews();
  };

  useOnMount(() => {
    const getVerifiedDietarySurveyResponses = async () => {
      const { data } = await GqlClient.query<DietaryDetailsForShefQuery>({
        query: DietaryDetailsForShefDocument,
        variables: { shefId: shef.id },
      });
      setDietaryDetails(data?.dietaryDetailsForShef);
    };
    getVerifiedDietarySurveyResponses();
  });

  return (
    <ShefDescriptionCardsContainer className={className}>
      {numRatings > 3 && (
        <ShefNumReviewsCard numRatings={numRatings} averageRating={averageRating} onClick={handleReviewsClick} />
      )}

      {totalOrders >= 10 && (
        <DescriptionCard
          headline={formatThousandsNumber(totalOrders)}
          icon={<BowlWithWhisk />}
          description='Meals prepared'
        />
      )}

      {!!dietaryDetails?.length &&
        dietaryDetails.map((details) => <DietaryDescriptionCard shef={shef} dietaryDetails={details} />)}

      {!!fhcApprovedAt && <FoodSafetyCard />}

      {mehkoLicenseNumber && (
        <DescriptionCard
          headline={mehkoLicenseNumber.replace('MEHKO License', '').trim()}
          description='MEHKO License'
        />
      )}
    </ShefDescriptionCardsContainer>
  );
};
